// /plans/:id — detalhe do plano com edição inline de entitlements + tab Histórico.
// Story 2.5b.

import { ArrowLeft, Copy, History, Layers, Package2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { CreatePlanEntitlementInput, UpdatePlanDto } from '@control-center/shared';

import { Field, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { LimitsOverrideField } from '../../components/plans/LimitsOverrideField';
import { PlanHistoryViewer } from '../../components/plans/PlanHistoryViewer';
import { useExternalPlans } from '../../hooks/use-external-plans';
import { usePlan, useUpdatePlan } from '../../hooks/use-plans';
import { useSaasProducts } from '../../hooks/use-saas-products';
import { extractErrorMessage } from '../../lib/errors';
import type { ExternalPlanEntity } from '../../lib/external-plans-api';
import type { SaasProductEntity } from '../../lib/saas-products-api';

type Tab = 'overview' | 'history';

export function PlanDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const planQ = usePlan(id);
  const update = useUpdatePlan();
  const [tab, setTab] = useState<Tab>('overview');
  const [entitlements, setEntitlements] = useState<CreatePlanEntitlementInput[]>([]);

  const saasList = useSaasProducts({ limit: 100 });
  const externalsList = useExternalPlans({ limit: 100 });
  const saasProducts = saasList.data?.items ?? [];
  const externalPlans = externalsList.data?.items ?? [];

  // Sincroniza estado local com fetched data
  useEffect(() => {
    if (planQ.data) {
      setEntitlements(
        planQ.data.entitlements.map((e) => ({
          saas_product_id: e.saasProductId,
          external_plan_id: e.externalPlanId,
          limits_override: (e.limitsOverride as Record<string, unknown> | null) ?? {},
          display_order: e.displayOrder,
        })),
      );
    }
  }, [planQ.data]);

  const dirty = useMemo(() => {
    if (!planQ.data) return false;
    const orig = planQ.data.entitlements;
    if (orig.length !== entitlements.length) return true;
    const origByKey = new Map(orig.map((e) => [e.saasProductId, e]));
    return entitlements.some((e) => {
      const o = origByKey.get(e.saas_product_id);
      if (!o) return true;
      return (
        o.externalPlanId !== e.external_plan_id ||
        o.displayOrder !== e.display_order ||
        JSON.stringify(o.limitsOverride ?? {}) !== JSON.stringify(e.limits_override ?? {})
      );
    });
  }, [planQ.data, entitlements]);

  async function saveEntitlements(): Promise<void> {
    if (!planQ.data) return;
    try {
      const payload: UpdatePlanDto = {
        version: planQ.data.version,
        entitlements: entitlements.map((e) => ({
          saas_product_id: e.saas_product_id,
          external_plan_id: e.external_plan_id,
          limits_override: e.limits_override,
          display_order: e.display_order ?? 0,
        })),
      } as UpdatePlanDto;
      await update.mutateAsync({ id, payload });
      toast.success('Entitlements salvos.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos salvar entitlements.'));
    }
  }

  if (planQ.isLoading) {
    return <div className="text-[13px] text-muted">Carregando…</div>;
  }
  if (planQ.isError || !planQ.data) {
    return (
      <div className="rounded-[14px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
        {extractErrorMessage(planQ.error, 'Plano não encontrado.')}
      </div>
    );
  }

  const plan = planQ.data;

  /**
   * Monta o link do checkout público deste plano e copia pro clipboard.
   * O painel e o checkout rodam no mesmo app → `window.location.origin` dá o
   * domínio certo sem hardcode. Mesmo padrão de `charges/index.tsx`.
   *
   * - `?saas=` (branding + resolução de namespace) só quando o plano ativa
   *   exatamente 1 SaaS (single_saas). Bundle/service_only omitem — sem SaaS único.
   * - `?cycle=anual` pré-seleciona o ciclo quando o plano é anual.
   */
  function copyCheckoutLink(): void {
    const params = new URLSearchParams();
    if (plan.entitlements.length === 1) {
      const saas = saasProducts.find((s) => s.id === plan.entitlements[0]?.saasProductId);
      if (saas) params.set('saas', saas.slug);
    }
    if (plan.billingCycle === 'anual') params.set('cycle', 'anual');
    const qs = params.toString();
    const url = `${window.location.origin}/checkout/${plan.slug}${qs ? `?${qs}` : ''}`;

    void navigator.clipboard.writeText(url).then(
      () => {
        if (plan.status === 'ativo') {
          toast.success('Link de pagamento copiado — envie ao cliente');
        } else {
          toast.warning(
            'Link copiado, mas o plano não está "ativo" — o cliente verá "plano indisponível" até você ativá-lo.',
          );
        }
      },
      () => toast.error('Não foi possível copiar o link'),
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/plans')}
            aria-label="Voltar"
            className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-line text-muted transition-colors hover:bg-panel-2"
          >
            <ArrowLeft size={14} strokeWidth={1.8} />
          </button>
          <div>
            <h1 className="m-0 text-[22px] font-bold tracking-[-0.025em] text-ink">{plan.name}</h1>
            <code className="text-[12px] text-muted-2">{plan.slug}</code>
          </div>
        </div>
        <PillButton
          icon={<Copy size={14} strokeWidth={1.8} />}
          type="button"
          onClick={copyCheckoutLink}
        >
          Copiar link de pagamento
        </PillButton>
      </header>

      <nav className="flex items-center gap-1 border-b border-line pb-px">
        <button
          type="button"
          onClick={() => setTab('overview')}
          className={`flex items-center gap-2 px-3 py-2 text-[13px] font-medium transition-colors border-b-2 -mb-px ${
            tab === 'overview'
              ? 'border-ink text-ink'
              : 'border-transparent text-muted hover:text-ink-2'
          }`}
        >
          <Package2 size={14} strokeWidth={1.8} />
          Visão geral
        </button>
        <button
          type="button"
          onClick={() => setTab('history')}
          className={`flex items-center gap-2 px-3 py-2 text-[13px] font-medium transition-colors border-b-2 -mb-px ${
            tab === 'history'
              ? 'border-ink text-ink'
              : 'border-transparent text-muted hover:text-ink-2'
          }`}
        >
          <History size={14} strokeWidth={1.8} />
          Histórico
        </button>
      </nav>

      {tab === 'overview' ? (
        <OverviewTab
          plan={plan}
          entitlements={entitlements}
          dirty={dirty}
          isSaving={update.isPending}
          saasProducts={saasProducts}
          externalPlans={externalPlans}
          onEntitlementsChange={setEntitlements}
          onSave={saveEntitlements}
          onReset={() => {
            if (planQ.data) {
              setEntitlements(
                planQ.data.entitlements.map((e) => ({
                  saas_product_id: e.saasProductId,
                  external_plan_id: e.externalPlanId,
                  limits_override: (e.limitsOverride as Record<string, unknown> | null) ?? {},
                  display_order: e.displayOrder,
                })),
              );
            }
          }}
        />
      ) : (
        <PlanHistoryViewer planId={id} />
      )}
    </div>
  );
}

// =============================================================================
// Overview tab
// =============================================================================

interface OverviewTabProps {
  plan: NonNullable<ReturnType<typeof usePlan>['data']>;
  entitlements: CreatePlanEntitlementInput[];
  dirty: boolean;
  isSaving: boolean;
  saasProducts: SaasProductEntity[];
  externalPlans: ExternalPlanEntity[];
  onEntitlementsChange: (next: CreatePlanEntitlementInput[]) => void;
  onSave: () => void | Promise<void>;
  onReset: () => void;
}

function OverviewTab(props: OverviewTabProps): JSX.Element {
  const {
    plan,
    entitlements,
    dirty,
    isSaving,
    saasProducts,
    externalPlans,
    onEntitlementsChange,
    onSave,
    onReset,
  } = props;

  const externalsBySaas = useMemo(() => {
    const map = new Map<string, ExternalPlanEntity[]>();
    for (const ext of externalPlans) {
      if (ext.status === 'descontinuado') continue;
      const list = map.get(ext.saasProductId) ?? [];
      list.push(ext);
      map.set(ext.saasProductId, list);
    }
    return map;
  }, [externalPlans]);

  const [invalidJsonIdx, setInvalidJsonIdx] = useState<Set<number>>(() => new Set());
  const hasInvalidJson = invalidJsonIdx.size > 0;

  function markJsonValidity(index: number, isValid: boolean): void {
    setInvalidJsonIdx((prev) => {
      const has = prev.has(index);
      if (isValid && !has) return prev;
      if (!isValid && has) return prev;
      const next = new Set(prev);
      if (isValid) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return (
    <div className="space-y-5">
      <section className="rounded-[14px] border border-line bg-panel p-5">
        <h3 className="m-0 mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Dados
        </h3>
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Item label="Tipo" value={plan.type} />
          <Item label="Status" value={plan.status} />
          <Item label="Ciclo" value={plan.billingCycle} />
          <Item label="Trial" value={`${plan.trialDays}d`} />
          <Item label="Mensal" value={formatBRL(plan.monthlyPrice)} />
          <Item label="Anual" value={plan.annualPrice ? formatBRL(plan.annualPrice) : '—'} />
          <Item label="Setup" value={formatBRL(plan.setupFee)} />
          <Item label="Promo" value={plan.promoPrice ? formatBRL(plan.promoPrice) : '—'} />
        </dl>

        {/* Destaques que o cliente vê no resumo do checkout público.
            Edição fica no modal da listagem (PlanForm) — aqui é leitura. */}
        <div className="mt-4 border-t border-line pt-4">
          <h4 className="m-0 mb-2 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
            Destaques do checkout
          </h4>
          {(plan.checkoutHighlights ?? []).length === 0 ? (
            <p className="m-0 text-[12px] text-muted">
              Nenhum configurado — o checkout usa os textos padrão.
            </p>
          ) : (
            <ul className="m-0 list-disc space-y-1 pl-5 text-[13px] text-ink-2">
              {/* key por índice: o schema não proíbe destaque duplicado */}
              {(plan.checkoutHighlights ?? []).map((h, i) => (
                <li key={`${i}-${h}`}>{h}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-[14px] border border-line bg-panel p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="m-0 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
            Entitlements ({entitlements.length})
          </h3>
          <PillButton
            icon={<Plus size={12} strokeWidth={2} />}
            type="button"
            onClick={() =>
              onEntitlementsChange([
                ...entitlements,
                {
                  saas_product_id: '',
                  external_plan_id: '',
                  limits_override: {},
                  display_order: entitlements.length,
                },
              ])
            }
          >
            Adicionar SaaS
          </PillButton>
        </div>

        {entitlements.length === 0 ? (
          <div className="rounded-[8px] border border-dashed border-line bg-panel-3/30 p-4 text-center text-[12px] text-muted">
            Sem entitlements (service_only).
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {entitlements.map((ent, idx) => {
              const options = ent.saas_product_id
                ? (externalsBySaas.get(ent.saas_product_id) ?? [])
                : [];
              return (
                <div key={idx} className="rounded-[8px] border border-line bg-panel-2 p-3">
                  <div className="flex items-start gap-3">
                    <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
                      <Layers size={14} strokeWidth={1.8} />
                    </span>
                    <div className="flex-1 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Field label={`SaaS #${idx + 1}`} required>
                        {({ inputId }) => (
                          <Select
                            id={inputId}
                            value={ent.saas_product_id}
                            onChange={(e) => {
                              const next = [...entitlements];
                              next[idx] = {
                                ...next[idx],
                                saas_product_id: e.target.value,
                                external_plan_id: '',
                              } as CreatePlanEntitlementInput;
                              onEntitlementsChange(next);
                            }}
                          >
                            <option value="" disabled>
                              Selecione…
                            </option>
                            {saasProducts.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </Select>
                        )}
                      </Field>
                      <Field label="Plano externo" required>
                        {({ inputId }) => (
                          <Select
                            id={inputId}
                            value={ent.external_plan_id}
                            onChange={(e) => {
                              const next = [...entitlements];
                              next[idx] = {
                                ...next[idx],
                                external_plan_id: e.target.value,
                              } as CreatePlanEntitlementInput;
                              onEntitlementsChange(next);
                            }}
                            disabled={!ent.saas_product_id || options.length === 0}
                          >
                            <option value="" disabled>
                              {options.length === 0 ? 'Nenhum plano disponível' : 'Selecione…'}
                            </option>
                            {options.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} ({p.slug})
                              </option>
                            ))}
                          </Select>
                        )}
                      </Field>
                    </div>
                    <button
                      type="button"
                      aria-label={`Remover entitlement ${idx + 1}`}
                      onClick={() => {
                        onEntitlementsChange(entitlements.filter((_, i) => i !== idx));
                        markJsonValidity(idx, true);
                      }}
                      className="mt-6 flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-bad"
                    >
                      <Trash2 size={14} strokeWidth={1.8} />
                    </button>
                  </div>
                  <div className="mt-3">
                    <LimitsOverrideField
                      value={(ent.limits_override as Record<string, unknown> | undefined) ?? {}}
                      onChange={(nextJson, isValid) => {
                        if (isValid) {
                          const next = [...entitlements];
                          next[idx] = {
                            ...next[idx],
                            limits_override: nextJson,
                          } as CreatePlanEntitlementInput;
                          onEntitlementsChange(next);
                        }
                        markJsonValidity(idx, isValid);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {dirty && (
          <div className="mt-4 flex items-center justify-end gap-2 border-t border-line pt-4">
            {hasInvalidJson && (
              <span className="text-[11px] text-red-300/80">Corrija o JSON de override.</span>
            )}
            <PillButton type="button" onClick={onReset} disabled={isSaving}>
              Descartar
            </PillButton>
            <PillButton
              solid
              type="button"
              onClick={() => void onSave()}
              disabled={isSaving || hasInvalidJson}
            >
              {isSaving ? 'Salvando…' : 'Salvar entitlements'}
            </PillButton>
          </div>
        )}
      </section>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function Item({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-3">{label}</dt>
      <dd className="mt-1 text-[13px] text-ink">{value}</dd>
    </div>
  );
}

function formatBRL(value: string | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return 'R$ —';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
